import useVelocityStore from 'src/stores/VelocityStore/useVelocityStore';
import useAccountData from 'src/hooks/useAccountData';
import { useCurrentAccountPositions } from 'src/hooks/query/accountTradingData/usePositions';
import {
	BASE_PRECISION_EXP,
	BigNum,
	MarketType,
	PRICE_PRECISION_EXP,
} from '@velocity-exchange/sdk';
import {
	MarketId,
	UIMarket,
	UIOrderType,
	getBaseAssetSymbol,
} from '@velocity-exchange/common';
import { capStringFractionDigits } from '@velocity-exchange/common/format';
import useShowAccountValues from 'src/hooks/useShowAccountValues';
import { useState } from 'react';
import useMarkPrice from 'src/hooks/useMarkPrice';
import {
	useMarketSizeDigits,
	useMarketStepSize,
} from 'src/hooks/useMarketStepSize';
import useClosePositionStateEngine from 'src/hooks/positions/useClosePositionStateEngine';
import useMarketsInfoStore from 'src/stores/useMarketsInfoStore';
import useVelocityClient from 'src/hooks/useVelocityClient';
import useCurrentSettings from 'src/hooks/useCurrentSettings';
import useOraclePrice from 'src/hooks/useOraclePrice';
import useExecuteClosePosition from 'src/hooks/positions/useExecuteClosePosition';
import useGetTickSizeForMarket, {
	getTickSizeDecimalPlaces,
} from 'src/hooks/useGetTickSizeForMarket';
import usePreviewOrder from 'src/components/TradeForm/hooks/usePreviewOrder';
import { parseInputBigNumOrZero } from 'src/utils/parseInputBigNum';

interface Params {
	marketIndex: number;
	uiOrderType: UIOrderType;
	onClosePositionSuccess: () => void;
}

/**
 * Hook shared by `ClosePositionModal` and `TpSlOrderFormPopup`.
 * Sets up / returns the bulk of what's needed to close a position.
 */
export default function useClosePositionUtils({
	marketIndex,
	uiOrderType,
	onClosePositionSuccess,
}: Params) {
	const marketId = new MarketId(marketIndex, MarketType.PERP);
	const uiMarket = UIMarket.createPerpMarket(marketIndex);

	const velocityClient = useVelocityClient();
	const userAccount = useAccountData();
	const showAccountValues = useShowAccountValues();
	const currentMarkPrice = useMarkPrice(marketId);
	const stepSize = useMarketStepSize(marketId);
	const sizeDigits = useMarketSizeDigits(marketId);
	const getTickSizeForMarket = useGetTickSizeForMarket();
	const [currentSettings, setCurrentSettings] = useCurrentSettings();
	const targetMarketInfo = useMarketsInfoStore((s) =>
		s.getMarketInfoByIndexAndType(marketIndex, MarketType.PERP)
	);
	const tradeFormSlippageTolerance = useVelocityStore(
		(s) => s.tradeForm.slippageTolerance
	);
	const oraclePrice = useOraclePrice(marketId);
	const openPerpPositions = useCurrentAccountPositions();
	const position = openPerpPositions.find(
		(openPosition) => openPosition.marketIndex === marketIndex
	);

	const openPositionSizeBigNum = BigNum.from(
		position?.baseSize.abs(),
		BASE_PRECISION_EXP
	);

	// Determine the close order side (opposite of position direction)
	const closeOrderSide: 'buy' | 'sell' =
		position?.direction === 'long' ? 'sell' : 'buy';

	// Use the position size for close order preview
	const positionSizeString = openPositionSizeBigNum.abs().toNum().toString();

	const previewOrderParams = usePreviewOrder(marketId, {
		baseSizeStringValue: positionSizeString,
		side: closeOrderSide,
		orderType: uiOrderType,
		reduceOnly: true, // Close orders are always reduce-only
	});

	const markPriceToUse = currentMarkPrice;

	const maxBaseSizeString = capStringFractionDigits(
		openPositionSizeBigNum.abs().toNum().toString(),
		{ maxFractionDigits: sizeDigits }
	);

	const tickSize = getTickSizeForMarket(marketId);
	const tickSizeDecimalPlaces = getTickSizeDecimalPlaces(tickSize);

	const [priceInputValue, setPriceInputValue] = useState(() =>
		markPriceToUse.toFixed(tickSizeDecimalPlaces)
	);
	const [secondaryPriceInputValue, setSecondaryPriceInputValue] = useState(() =>
		markPriceToUse.toFixed(tickSizeDecimalPlaces)
	);
	const [baseSizeInputValue, setBaseSizeInputValue] =
		useState(maxBaseSizeString);

	const baseAssetSymbol = getBaseAssetSymbol(position?.marketSymbol ?? '');

	const baseSizeInputValueBigNum = parseInputBigNumOrZero(
		baseSizeInputValue,
		BASE_PRECISION_EXP
	);

	const priceInputValueBigNum = parseInputBigNumOrZero(
		priceInputValue,
		PRICE_PRECISION_EXP
	);

	const secondaryPriceInputValueBigNum = parseInputBigNumOrZero(
		secondaryPriceInputValue,
		PRICE_PRECISION_EXP
	);

	const {
		errorMessageInfo,
		showSecondaryPriceBox,
		priceBoxHeading,
		quoteSizeOfClose: autoCalculatedQuoteSize,
		priceImpactInfo,
	} = useClosePositionStateEngine({
		market: marketIndex,
		selectedOrderType: uiOrderType,
		priceBoxValue: priceInputValueBigNum,
		secondaryPriceBoxValue: secondaryPriceInputValueBigNum,
		currentPositionDirection: position?.direction ?? 'long',
		baseAmountOfClose: baseSizeInputValueBigNum,
	});

	const slippage = previewOrderParams.slippage;

	const { closePosition, isPending: isClosingPosition } =
		useExecuteClosePosition({
			marketIndex,
			uiOrderType,
			baseSize: baseSizeInputValueBigNum,
			onSuccess: onClosePositionSuccess,
			limitPrice:
				uiOrderType !== 'market'
					? uiOrderType === 'takeProfitLimit' || uiOrderType === 'stopLimit'
						? secondaryPriceInputValueBigNum
						: priceInputValueBigNum
					: undefined,
			triggerPrice:
				uiOrderType === 'takeProfitLimit' || uiOrderType === 'stopLimit'
					? priceInputValueBigNum
					: undefined,
			shouldCaptureEvent: true,
		});

	return {
		closePosition,
		isClosingPosition,
		slippage,
		showAccountValues,
		currentSettings,
		setCurrentSettings,
		priceInputValue,
		setPriceInputValue,
		baseSizeInputValue,
		setBaseSizeInputValue,
		baseSizeInputValueBigNum,
		openPositionSizeBigNum,
		maxBaseSizeString,
		baseAssetSymbol,
		markPriceToUse,
		position,
		stepSize,
		sizeDigits,
		autoCalculatedQuoteSize,
		errorMessageInfo,
		priceImpactInfo,
		// The following are only used for `TpSlOrderFormPopup`.
		closeOrderSide,
		uiMarket,
		marketId,
		secondaryPriceInputValue,
		setSecondaryPriceInputValue,
		priceBoxHeading,
		showSecondaryPriceBox,
		velocityClient,
		tradeFormSlippageTolerance,
		targetMarketInfo,
		userAccount,
		oraclePrice,
		currentMarkPrice,
	};
}
